import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CaptionSchedule, CaptionScheduleIn, Device, ShowEvent } from '@glyph/protocol-ts';
import { useDeviceSchedule, useDevices, useEvents, useMe, useSchedules } from '@glyph/core';
import { Button, Card, CellMono, CellText, CellTitle, Checkbox, DataTable, Dialog, EmptyState, IconButton, Note, PageHeader, Section, Select, StatusPill, Switch, Tag, TextInput, useToast } from '@glyph/ui';

/** Who may create or change schedules (the cloud enforces the same: `schedules.manage` = admin and up). */
const MANAGE = new Set(['owner', 'admin', 'platform']);
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

const browserTz = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};
/** The zones a venue is likely in, plus the browser's own and whatever a schedule already uses. */
const COMMON_TZ = ['UTC', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'Asia/Dubai', 'Asia/Singapore', 'Australia/Sydney'];
const tzOptions = (current: string) => {
  const set = new Set([browserTz(), ...COMMON_TZ, current]);
  return [...set].filter(Boolean).map((z) => ({ value: z, label: z.replace(/_/g, ' ') }));
};

export const blankSchedule = (deviceIds: string[] = []): CaptionScheduleIn => ({
  name: '',
  enabled: true,
  tz: browserTz(),
  start_local: '18:00',
  end_local: '07:30',
  days: [...ALL_DAYS],
  device_ids: [...deviceIds],
  event_ids: [],
});
const editOf = (s: CaptionSchedule): CaptionScheduleIn => ({ name: s.name, enabled: s.enabled, tz: s.tz, start_local: s.start_local, end_local: s.end_local, days: [...s.days], device_ids: [...s.device_ids], event_ids: [...s.event_ids] });

/** "Mon–Fri", "every day", "Sat, Sun". */
export const fmtDays = (days: number[]) => {
  const d = [...new Set(days)].sort((a, b) => a - b);
  if (d.length === 7) return 'every day';
  if (d.length === 5 && d.every((x, i) => x === i)) return 'Mon–Fri';
  return d.map((x) => DAYS[x] ?? '?').join(', ');
};
export const fmtWindow = (s: Pick<CaptionSchedule, 'start_local' | 'end_local' | 'tz'>) => `${s.start_local} → ${s.end_local} · ${s.tz.replace(/_/g, ' ')}`;
/** A moment up to a day away as "07:30", further out as "Fri 18:00". */
const fmtUntil = (ts: number) => {
  const d = new Date(ts * 1000);
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  return ts - Date.now() / 1000 < 22 * 3600 ? time : `${d.toLocaleDateString('en-GB', { weekday: 'short' })} ${time}`;
};

/**
 * Caption schedules (admin tab): after-hours OFF windows so idle units stop streaming to Deepgram. The cloud
 * sends engine.stop / engine.start at the edges and again when a unit reconnects; a Captions ON/OFF press on a
 * unit holds until the next edge. Nothing runs on the unit.
 */
export function SchedulesScreen({ tabs }: { tabs?: ReactNode }) {
  const me = useMe();
  const schedules = useSchedules();
  const devices = useDevices();
  const events = useEvents();
  const toast = useToast();
  const canManage = MANAGE.has(me.data?.role ?? '');
  const list = schedules.data ?? [];
  const devs = devices.data ?? [];
  const shows = events.data ?? [];
  const [editing, setEditing] = useState<{ id: string | null; body: CaptionScheduleIn } | null>(null);
  const [removing, setRemoving] = useState<CaptionSchedule | null>(null);
  const nameOf = (ids: string[], pool: { id: string; name: string }[]) => ids.map((id) => pool.find((x) => x.id === id)?.name ?? '?');
  const targetsOf = (s: CaptionSchedule) => {
    const parts = [...nameOf(s.event_ids, shows).map((n) => `${n} (group)`), ...nameOf(s.device_ids, devs)];
    return parts.join(', ') || '—';
  };
  const toggleEnabled = async (s: CaptionSchedule, on: boolean) => {
    try {
      await schedules.update({ id: s.id, body: { ...editOf(s), enabled: on } });
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const remove = async () => {
    if (!removing) return;
    try {
      await schedules.remove(removing.id);
      setRemoving(null);
      toast.push(`${removing.name} deleted · its units come back on at the next check`, 'ok');
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const offNow = list.filter((s) => s.enabled && s.active_now).length;
  const newBtn = canManage ? (
    <Button variant="primary" size="responsive" onClick={() => setEditing({ id: null, body: blankSchedule() })}>
      New schedule
    </Button>
  ) : null;
  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Schedules"
        meta={`${list.length} SCHEDULE${list.length === 1 ? '' : 'S'} · ${offNow} OFF NOW`}
        actions={
          <>
            {tabs}
            {newBtn}
          </>
        }
      />
      <div className="flex items-center justify-between gap-3 md:hidden">
        {tabs}
        {newBtn}
      </div>
      <Card>
        <DataTable
          rows={list}
          rowKey={(s) => s.id}
          minWidth={760}
          hideOnMobile={false}
          empty={<EmptyState label="No schedules yet" action={newBtn ?? undefined} />}
          columns={[
            { key: 'name', header: 'Schedule', width: '1.4fr', cell: (s) => <CellTitle title={s.name} sub={fmtDays(s.days)} /> },
            {
              key: 'state',
              header: 'State',
              width: '.8fr',
              cell: (s) => <StatusPill state={!s.enabled ? 'offline' : s.active_now ? 'live' : 'standby'}>{!s.enabled ? 'disabled' : s.active_now ? 'off now' : 'armed'}</StatusPill>,
            },
            { key: 'window', header: 'Captions off', width: '1.6fr', cell: (s) => <CellMono>{fmtWindow(s)}</CellMono> },
            { key: 'targets', header: 'Units & groups', width: '1.6fr', cell: (s) => <CellText>{targetsOf(s)}</CellText> },
            {
              key: 'act',
              header: '',
              width: canManage ? '210px' : '0px',
              align: 'right',
              cell: (s) =>
                canManage ? (
                  <div className="flex items-center justify-end gap-2" onClick={(ev) => ev.stopPropagation()}>
                    <Switch checked={s.enabled} onChange={(v) => void toggleEnabled(s, v)} label={`${s.enabled ? 'Disable' : 'Enable'} ${s.name}`} disabled={schedules.busy} />
                    <Button size="sm" onClick={() => setEditing({ id: s.id, body: editOf(s) })}>
                      Edit
                    </Button>
                    <IconButton framed={false} danger label={`Delete ${s.name}`} className="text-xl text-fg5" onClick={() => setRemoving(s)}>
                      ×
                    </IconButton>
                  </div>
                ) : null,
            },
          ]}
        />
      </Card>
      <Note>
        Inside a window the cloud stops captioning on each unit and starts it again at the end; a unit that reconnects (power cut, reboot) is stopped again within seconds. Pressing Captions ON or OFF on a unit holds until the next
        edge. Recording, display and the hotspot are untouched.
      </Note>
      <ScheduleEditor
        open={!!editing}
        onClose={() => setEditing(null)}
        id={editing?.id ?? null}
        initial={editing?.body ?? null}
        devices={devs}
        shows={shows}
        onSaved={(s, created) => {
          setEditing(null);
          toast.push(created ? `${s.name} created` : `${s.name} saved`, 'ok');
        }}
      />
      <Dialog open={!!removing} onClose={() => setRemoving(null)} title={removing ? `Delete ${removing.name}?` : ''} confirm={{ label: 'Delete', destructive: true, onClick: remove, busy: schedules.busy }}>
        Units it keeps off come back on at the next check (within a minute).
      </Dialog>
    </Section>
  );
}

/** The editor: name, on/off, window (local times + zone), days, units, groups. Used by the admin tab and the Devices page. */
export function ScheduleEditor({
  open,
  onClose,
  id,
  initial,
  devices,
  shows,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  /** null = create. */
  id: string | null;
  initial: CaptionScheduleIn | null;
  devices: Device[];
  shows: ShowEvent[];
  onSaved: (s: CaptionSchedule, created: boolean) => void;
}) {
  const schedules = useSchedules(false);
  const toast = useToast();
  const [body, setBody] = useState<CaptionScheduleIn>(initial ?? blankSchedule());
  useEffect(() => {
    if (open) setBody(initial ?? blankSchedule());
  }, [open, initial]);
  const set = (patch: Partial<CaptionScheduleIn>) => setBody((b) => ({ ...b, ...patch }));
  const toggleIn = (key: 'device_ids' | 'event_ids', v: string, on: boolean) => set({ [key]: on ? [...new Set([...body[key], v])] : body[key].filter((x) => x !== v) } as Partial<CaptionScheduleIn>);
  const toggleDay = (d: number, on: boolean) => set({ days: on ? [...new Set([...body.days, d])].sort((a, b) => a - b) : body.days.filter((x) => x !== d) });
  const valid = !!body.name.trim() && body.days.length > 0 && body.start_local !== body.end_local && (body.device_ids.length > 0 || body.event_ids.length > 0);
  const wraps = body.start_local > body.end_local;
  const save = async () => {
    if (!valid) return;
    const clean = { ...body, name: body.name.trim() };
    try {
      const s = id ? await schedules.update({ id, body: clean }) : await schedules.create(clean);
      onSaved(s, !id);
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  const units = useMemo(() => [...devices].filter((d) => d.status !== 'pending').sort((a, b) => a.name.localeCompare(b.name)), [devices]);
  if (!open) return null;
  return (
    <Dialog open onClose={onClose} title={id ? 'Edit schedule' : 'New schedule'} meta="CAPTIONS OFF BETWEEN THESE TIMES · UNITS STOP STREAMING TO DEEPGRAM" size="lg" confirm={{ label: id ? 'Save' : 'Create', onClick: save, disabled: !valid, busy: schedules.busy }}>
      <div className="flex flex-col gap-4">
        <div className="flex items-end gap-3">
          <label className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="micro text-fg3">Name</span>
            <TextInput value={body.name} onChange={(e) => set({ name: e.target.value })} block autoFocus placeholder="After hours" />
          </label>
          <Switch checked={body.enabled} onChange={(v) => set({ enabled: v })} label="Enabled" className="mb-1" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="flex flex-col gap-2">
            <span className="micro text-fg3">Captions off from</span>
            <TextInput type="time" value={body.start_local} onChange={(e) => e.target.value && set({ start_local: e.target.value })} block aria-label="Captions off from" />
          </label>
          <label className="flex flex-col gap-2">
            <span className="micro text-fg3">Back on at</span>
            <TextInput type="time" value={body.end_local} onChange={(e) => e.target.value && set({ end_local: e.target.value })} block aria-label="Back on at" />
          </label>
          <label className="flex flex-col gap-2">
            <span className="micro text-fg3">Time zone</span>
            <Select value={body.tz} onChange={(v) => set({ tz: v })} options={tzOptions(body.tz)} block aria-label="Time zone" />
          </label>
        </div>
        <Note>{wraps ? 'Runs overnight: off in the evening, back on the next morning.' : 'Off and back on the same day.'} Days are the days the window starts.</Note>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {DAYS.map((label, i) => (
            <label key={label} className="flex items-center gap-2 text-base">
              <Checkbox checked={body.days.includes(i)} onChange={(v) => toggleDay(i, v)} label={label} />
              <span>{label}</span>
            </label>
          ))}
        </div>
        {shows.length > 0 && (
          <div className="flex flex-col gap-1">
            <span className="micro text-fg3">Groups (every unit on the group, now and later)</span>
            {shows.map((e) => (
              <label key={e.id} className="flex items-center gap-2 py-1 text-base">
                <Checkbox checked={body.event_ids.includes(e.id)} onChange={(v) => toggleIn('event_ids', e.id, v)} label={`Group ${e.name}`} />
                <span className="min-w-0 flex-1 truncate">{e.name}</span>
                <span className="font-mono text-xs text-fg4">{devices.filter((d) => d.assignment?.event_id === e.id).length} UNITS</span>
              </label>
            ))}
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="micro text-fg3">Units</span>
          {units.length === 0 && <Note>No units enrolled yet.</Note>}
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2">
            {units.map((d) => (
              <label key={d.id} className="flex items-center gap-2 py-1 text-base">
                <Checkbox checked={body.device_ids.includes(d.id)} onChange={(v) => toggleIn('device_ids', d.id, v)} label={`Unit ${d.name}`} />
                <span className="min-w-0 flex-1 truncate">{d.name}</span>
                <span className="font-mono text-xs text-fg4">{d.assignment?.event_name ?? ''}</span>
              </label>
            ))}
          </div>
        </div>
        {!valid && <Note tone="warn">{!body.name.trim() ? 'Give it a name.' : body.days.length === 0 ? 'Pick at least one day.' : body.start_local === body.end_local ? 'Start and end are the same.' : 'Pick at least one unit or group.'}</Note>}
      </div>
    </Dialog>
  );
}

/** Devices page: put the ticked units on an existing schedule, or start a new one with them ticked. */
export function AddToScheduleDialog({ open, onClose, targets }: { open: boolean; onClose: () => void; targets: Device[] }) {
  const schedules = useSchedules(open);
  const devices = useDevices();
  const events = useEvents();
  const toast = useToast();
  const [pick, setPick] = useState('');
  const [creating, setCreating] = useState(false);
  useEffect(() => {
    if (open) {
      setPick('');
      setCreating(false);
    }
  }, [open]);
  const list = schedules.data ?? [];
  const options = [{ value: '', label: list.length ? 'Pick a schedule…' : 'No schedules yet' }, ...list.map((s) => ({ value: s.id, label: `${s.name} · ${s.start_local} → ${s.end_local}` }))];
  const add = async () => {
    const s = list.find((x) => x.id === pick);
    if (!s) return;
    try {
      await schedules.update({ id: s.id, body: { ...editOf(s), device_ids: [...new Set([...s.device_ids, ...targets.map((d) => d.id)])] } });
      toast.push(`${targets.length} unit${targets.length === 1 ? '' : 's'} added to ${s.name}`, 'ok');
      onClose();
    } catch (e) {
      toast.push((e as Error).message, 'warn');
    }
  };
  if (!open) return null;
  if (creating)
    return (
      <ScheduleEditor
        open
        onClose={onClose}
        id={null}
        initial={blankSchedule(targets.map((d) => d.id))}
        devices={devices.data ?? []}
        shows={events.data ?? []}
        onSaved={(s) => {
          toast.push(`${s.name} created with ${s.device_ids.length} unit${s.device_ids.length === 1 ? '' : 's'}`, 'ok');
          onClose();
        }}
      />
    );
  return (
    <Dialog open onClose={onClose} title={`Schedule ${targets.length} unit${targets.length === 1 ? '' : 's'}`} meta="CAPTIONS OFF AFTER HOURS · NO STREAMING WHILE OFF" confirm={{ label: 'Add', onClick: add, disabled: !pick, busy: schedules.busy }}>
      <div className="flex flex-col gap-4">
        <span className="font-mono text-xs text-fg4">{targets.map((d) => d.name).join(', ')}</span>
        <label className="flex flex-col gap-2">
          <span className="micro text-fg3">Add to</span>
          <Select value={pick} onChange={setPick} options={options} block aria-label="Add to schedule" disabled={!list.length} />
        </label>
        <div>
          <Button size="sm" onClick={() => setCreating(true)}>
            New schedule with these units…
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Next to the Captions toggle: "SCHEDULED OFF · ON AT 07:30", "OFF FROM 18:00", or an override notice. Nothing when no schedule names the unit. */
export function ScheduleBadge({ deviceId, className }: { deviceId?: string; className?: string }) {
  const q = useDeviceSchedule(deviceId);
  const s = q.data;
  if (!s || !s.desired) return null;
  if (s.desired === 'off') {
    if (s.override) return <Tag tone="warn" className={className}>{`SCHEDULE OVERRIDDEN${s.until ? ` · OFF UNTIL ${fmtUntil(s.until)}` : ''}`}</Tag>;
    return <Tag tone="warn" className={className}>{`SCHEDULED OFF${s.until ? ` · ON AT ${fmtUntil(s.until)}` : ''}${s.applied ? '' : ' · PENDING'}`}</Tag>;
  }
  return s.until ? <Tag tone="fg4" className={className}>{`OFF FROM ${fmtUntil(s.until)}`}</Tag> : null;
}
